import { useCallback, useEffect, useState } from 'react';
import { type UploadProps } from 'antd';
import Upload, { UploadFile } from 'antd/es/upload';
import Dragger from 'antd/es/upload/Dragger';
import { ReactComponent as UploadDocumentIcon } from '@Icons/additional/upload-document.svg';
import { useAppSelector } from '@Hooks/index';
import { isSavedUploadedFileSelector } from '@Store/selectors/fileUploadSelector';
import { useDownloadDocumentQuery } from '@Store/api/dmsApiSlice';
import { Attachment } from '@Components/types/Attachment';
import { RcCustomRequestOptions } from '@Components/types/UploadType';

import { formatBytes } from '@Utils/formatBytes';

import UploadedFile from './components/UploadedFile';

import styles from './upload.module.css';

interface MUploadProps {
  disabled?: boolean;
  maxCount?: number;
  uploadedFile?: Attachment;
  showSwitcher?: boolean;
  onUploadedFileRemove?: (value: boolean | undefined) => void;
  onFileRequest?: (options: RcCustomRequestOptions) => void;
  onFileRemove?: () => void;
}

interface ValidFile {
  isValidFileType: boolean;
  isValidFileSize: boolean;
  validationTypeMessage: string;
  validationSizeMessage: string;
}

const defaultFile: ValidFile = {
  isValidFileSize: true,
  isValidFileType: true,
  validationSizeMessage: '',
  validationTypeMessage: '',
};

const MUpload: React.FC<MUploadProps> = ({
  disabled,
  onFileRequest,
  maxCount = 1,
  onFileRemove,
  uploadedFile,
  onUploadedFileRemove,
  showSwitcher,
}) => {
  const { data: imageURL } = useDownloadDocumentQuery(uploadedFile?.id || '', {
    skip: !uploadedFile?.id,
  });
  const isSavedFiled = useAppSelector(isSavedUploadedFileSelector);

  const [validationFile, setValidationFile] = useState(defaultFile);

  const [fileList, setFileList] = useState(
    // eslint-disable-next-line
    [] as UploadFile<any>[]
  );

  useEffect(() => {
    if (!uploadedFile && isSavedFiled) {
      setFileList([]);
    }
  }, [uploadedFile, isSavedFiled]);

  const isSingleFileMode = maxCount === 1;

  const shouldRemove = (isSingleFileMode && !!fileList.length) || undefined;

  const handleDownloadClick = useCallback((): void => {
    if (imageURL && uploadedFile) {
      const a = document.createElement('a');
      a.href = imageURL;
      a.download = uploadedFile?.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  }, [imageURL, uploadedFile]);

  const onBeforeUpload: UploadProps['beforeUpload'] = (file) => {
    const isAllowedSize = formatBytes(file.size) <= 25;
    const isAllowedType =
      file.type === 'image/png' ||
      file.type === 'image/jpeg' ||
      file.type === 'application/pdf';

    if (!isAllowedType) {
      setValidationFile((prev) => ({
        ...prev,
        isValidFileType: false,
        validationTypeMessage: `${file.name} is not a correct file type`,
      }));
    } else {
      setValidationFile((prev) => ({
        ...prev,
        isValidFileType: true,
        validationTypeMessage: '',
      }));
    }

    if (!isAllowedSize) {
      setValidationFile((prev) => ({
        ...prev,
        isValidFileSize: false,
        validationSizeMessage: `${file.name} is not a correct file size`,
      }));
    } else {
      setValidationFile((prev) => ({
        ...prev,
        isValidFileSize: true,
        validationSizeMessage: '',
      }));
    }

    return (isAllowedType && isAllowedSize) || Upload.LIST_IGNORE;
  };

  return (
    <>
      <Dragger
        className={styles.content}
        customRequest={onFileRequest}
        onChange={({ fileList: fileListNew }) => {
          const fileAdded = !fileList.length && Boolean(fileListNew.length);
          const fileRemoved = Boolean(fileList.length) && !fileListNew.length;

          if (isSingleFileMode && fileAdded) {
            onUploadedFileRemove?.(true);
          }

          if (isSingleFileMode && fileRemoved) {
            onUploadedFileRemove?.(false);
          }

          setFileList(fileListNew);
        }}
        fileList={fileList}
        disabled={disabled}
        maxCount={maxCount}
        onRemove={onFileRemove}
        beforeUpload={onBeforeUpload}
      >
        <div className="ant-upload-drag-icon">
          <UploadDocumentIcon className={styles.uploadIcon} />
        </div>
        <div className={styles.uploadText}>
          Click or drag file to this area to upload
        </div>
        <div className={styles.uploadHintText}>
          Supports JPEG, PNG, PDF & Max Size 25MB
        </div>
      </Dragger>
      {!validationFile.isValidFileType && (
        <div className={styles.validation}>
          {validationFile.validationTypeMessage}
        </div>
      )}
      {!validationFile.isValidFileSize && (
        <div className={styles.validation}>
          {validationFile.validationSizeMessage}
        </div>
      )}
      <UploadedFile
        uploadedFile={uploadedFile}
        onUploadedFileRemove={onUploadedFileRemove}
        handleDownloadClick={handleDownloadClick}
        shouldRemove={shouldRemove}
        showSwitcher={showSwitcher}
      />
    </>
  );
};

export default MUpload;
